/**
 * Price Deviation Detection Composable
 *
 * Detects and tracks the price difference of the whole order between Player API
 * prices (shown on Offer page) and actual checkout prices (confirmed via availability check).
 *
 * Usage:
 * 1. Initialize after successful availability check in CheckoutPage
 * 2. Access computed deviation for the total order price
 */

import { ref, computed, type ComputedRef, type Ref } from 'vue';
import {
  loadOfferPrices,
  matchesCurrentOffer,
  type StoredOfferPrices,
  type OfferIdentifier,
} from '@/utils/checkout/offerPriceStorage';

// ============================================================================
// Types
// ============================================================================

/**
 * Parameters for initializing price deviation detection
 */
export interface InitializePriceDeviationParams {
  identifier: OfferIdentifier;
  totalPrice: number;
}

/**
 * Complete price deviation result
 */
export interface PriceDeviationResult {
  hasDeviation: boolean;
  totalDeviation: number;
  originalTotalPrice: number;
  currentTotalPrice: number;
}

// ============================================================================
// Composable
// ============================================================================

/**
 * Price deviation detection composable
 *
 * @returns Reactive state and methods for detecting price deviations
 *
 * @example
 * const { initialize, priceDeviationResult } = usePriceDeviation();
 *
 * // After availability check success
 * initialize({ identifier, totalPrice: currentTotalPrice });
 *
 * // Check for deviations
 * if (priceDeviationResult.value?.hasDeviation) {
 *   console.log('Price changed by', priceDeviationResult.value.totalDeviation);
 * }
 */
export function usePriceDeviation() {
  const storedPrices: Ref<StoredOfferPrices | null> = ref(null);
  const currentTotalPrice: Ref<number> = ref(0);
  const isInitialized = ref(false);

  // Methods
  function initialize(params: InitializePriceDeviationParams): void {
    const { identifier, totalPrice } = params;

    currentTotalPrice.value = totalPrice;

    const stored = loadOfferPrices();

    if (stored && matchesCurrentOffer(stored, identifier)) {
      storedPrices.value = stored;
    } else {
      storedPrices.value = null;
    }

    isInitialized.value = true;
  }

  // Computed Properties
  const priceDeviationResult: ComputedRef<PriceDeviationResult | null> = computed(() => {
    if (!isInitialized.value || !storedPrices.value) {
      return null;
    }

    const stored = storedPrices.value;
    const totalDeviation = currentTotalPrice.value - stored.totalPrice;

    // Hide deviations within ±3 EUR; only meaningful changes are surfaced
    const hasDeviation = Math.abs(totalDeviation) > 3;

    return {
      hasDeviation,
      totalDeviation: hasDeviation ? totalDeviation : 0, // Normalize to 0 if within tolerance
      originalTotalPrice: stored.totalPrice,
      currentTotalPrice: currentTotalPrice.value,
    };
  });

  const hasAnyDeviation: ComputedRef<boolean> = computed(() => {
    return priceDeviationResult.value?.hasDeviation ?? false;
  });
  return {
    // State
    isInitialized,
    storedPrices,

    // Computed
    priceDeviationResult,
    hasAnyDeviation,

    // Methods
    initialize,
  };
}
